import {
  AppSignUpModalVariant,
  isFaireDirectVariant,
} from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpModalVariant";
import { EmailInput } from "@faire/retailer-visitor-shared/components/AppSignUp/components/EmailInput";
import { SignUpCTA } from "@faire/retailer-visitor-shared/components/AppSignUp/components/SignUpCTA";
import { useBuildSignUpUrl } from "@faire/retailer-visitor-shared/components/AppSignUp/useBuildSignUpURL";
import { IFormField } from "@faire/retailer-visitor-shared/components/SignUpForm/types";
import { useFetchHCaptchaToken } from "@faire/retailer-visitor-shared/hooks/hCaptcha/useFetchHCaptchaToken";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { useSettingPrefetchSignUpIframe } from "@faire/retailer-visitor-shared/settings/useSettingPrefetchSignUpIframe";
import { useSettingSignUpSplit } from "@faire/retailer-visitor-shared/settings/useSettingSignUpSplit";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { useInjectIframePrefetchCallback } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useInjectIframePrefetch";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Spacer } from "@faire/web--source/slate/spacing";
import { trackSignupModalSubmitEmailClickClick } from "@faire/web-api--source/events/signup/click/modalSubmitEmailClick";
import { QueryParameters as SignUpQueryParameters } from "@faire/web-api--source/routes/www/welcome/r/personal";
import React from "react";

interface IAppSignUpFormProps {
  email: IFormField<string>;
  modalVariant: AppSignUpModalVariant;
  omitLabel?: boolean;
  isSimplifiedSemDiscoverV2?: boolean;
  handleSubmit: (
    e?: React.FormEvent,
    queryParamsOverride?: Partial<SignUpQueryParameters>
  ) => void;
  setEmailValidationErrors: () => boolean;
  markUserEntersEmail: () => void;
  onSignUp?: () => void;
}

export const AppSignUpForm: React.FC<IAppSignUpFormProps> = ({
  email,
  modalVariant,
  omitLabel,
  handleSubmit: _handleSubmit,
  setEmailValidationErrors,
  markUserEntersEmail,
  onSignUp,
  isSimplifiedSemDiscoverV2,
}) => {
  const isBrandPreview = getIsBrandPreview();
  const { reset: resetSSO } = useSSOStore();
  const isSSR = useIsSSR();
  const isSignUpSplitEnabled = useSettingSignUpSplit();
  const isPrefetchSignUpIframeEnabled = useSettingPrefetchSignUpIframe();
  const signUpUrl = useBuildSignUpUrl();
  const injectIframePrefetchCallback =
    useInjectIframePrefetchCallback(signUpUrl);

  const { fetchHCaptchaToken } = useFetchHCaptchaToken();
  const handleSubmit = async (
    e?: React.FormEvent,
    queryParamsOverride?: Partial<SignUpQueryParameters>
  ) => {
    trackSignupModalSubmitEmailClickClick();
    onSignUp?.();

    // In the case where the user initially wanted to sign up with
    // SSO but changed their minds, we do not want to populate signup fields
    // with stale SSO data.
    resetSSO();

    return fetchHCaptchaToken(true).then(() =>
      _handleSubmit(e, queryParamsOverride)
    );
  };

  return (
    <>
      <EmailInput
        email={email}
        onContinue={handleSubmit}
        onBlur={setEmailValidationErrors}
        disabled={isBrandPreview}
        markUserEntersEmail={markUserEntersEmail}
        omitLabel={omitLabel}
        onFocus={() =>
          injectIframePrefetchCallback(
            !isPrefetchSignUpIframeEnabled || !isSignUpSplitEnabled
          )
        }
      />
      <Spacer size="2x" />
      <SignUpCTA
        handleSubmit={handleSubmit}
        disabled={isBrandPreview || isSSR}
        isFaireDirect={isFaireDirectVariant(modalVariant)}
        isSimplifiedSemDiscoverV2={isSimplifiedSemDiscoverV2}
      />
    </>
  );
};
